/**
 * Button.tsx
 *
 * Variants: primary (cyan, glowing), secondary (outlined), ghost (minimal), danger
 * Sizes: sm, md (default), lg
 * States: default, hover, active, disabled, loading
 *
 * Primary button has a subtle glow — it should be used sparingly (max 1 per view).
 */

import { ButtonHTMLAttributes, ReactNode, forwardRef } from 'react';
import { theme } from '../../theme';

type Variant = 'primary' | 'secondary' | 'ghost' | 'danger';
type Size = 'sm' | 'md' | 'lg';

interface ButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children'> {
  variant?: Variant;
  size?: Size;
  loading?: boolean;
  leftIcon?: ReactNode;
  rightIcon?: ReactNode;
  fullWidth?: boolean;
  children?: ReactNode;
}

const sizeMap = {
  sm: { padding: '5px 10px', fontSize: '12px', gap: 5, iconSize: 12 },
  md: { padding: '8px 14px', fontSize: '13px', gap: 6, iconSize: 14 },
  lg: { padding: '11px 20px', fontSize: '14px', gap: 8, iconSize: 16 },
};

function getVariantStyles(variant: Variant, disabled: boolean) {
  if (disabled) {
    return {
      background: theme.surface.card,
      color: theme.text.quaternary,
      border: `1px solid ${theme.borderColor.default}`,
      boxShadow: 'none',
      cursor: 'not-allowed' as const,
    };
  }
  switch (variant) {
    case 'primary':
      return {
        background: theme.brand.cyan,
        color: theme.text.inverse,
        border: `1px solid ${theme.brand.cyan}`,
        boxShadow: theme.shadow.glow,
        fontWeight: theme.fontWeight.semibold,
      };
    case 'secondary':
      return {
        background: theme.surface.card,
        color: theme.text.primary,
        border: `1px solid ${theme.borderColor.default}`,
        boxShadow: 'none',
        fontWeight: theme.fontWeight.medium,
      };
    case 'ghost':
      return {
        background: 'transparent',
        color: theme.text.secondary,
        border: '1px solid transparent',
        boxShadow: 'none',
        fontWeight: theme.fontWeight.medium,
      };
    case 'danger':
      return {
        background: theme.semantic.dangerSoft,
        color: theme.semantic.danger,
        border: `1px solid ${theme.semantic.dangerBorder}`,
        boxShadow: 'none',
        fontWeight: theme.fontWeight.medium,
      };
  }
}

function getHoverStyles(variant: Variant) {
  switch (variant) {
    case 'primary':
      return { background: theme.brand.cyanLight, borderColor: theme.brand.cyanLight };
    case 'secondary':
      return { background: theme.surface.cardHover, borderColor: theme.borderColor.strong };
    case 'ghost':
      return { background: 'rgba(255,255,255,0.03)', color: theme.text.primary };
    case 'danger':
      return { background: 'rgba(239, 68, 68, 0.16)', borderColor: theme.semantic.danger };
  }
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant = 'secondary',
    size = 'md',
    loading = false,
    leftIcon,
    rightIcon,
    fullWidth,
    disabled,
    children,
    style,
    onMouseEnter,
    onMouseLeave,
    ...rest
  },
  ref,
) {
  const isDisabled = disabled || loading;
  const sizing = sizeMap[size];
  const variantStyles = getVariantStyles(variant, isDisabled);

  return (
    <button
      ref={ref}
      disabled={isDisabled}
      onMouseEnter={e => {
        if (!isDisabled) {
          const hover = getHoverStyles(variant);
          Object.assign(e.currentTarget.style, hover);
        }
        onMouseEnter?.(e);
      }}
      onMouseLeave={e => {
        if (!isDisabled) {
          Object.assign(e.currentTarget.style, {
            background: variantStyles.background,
            borderColor: variantStyles.border.split(' ').pop(),
            color: variantStyles.color,
          });
        }
        onMouseLeave?.(e);
      }}
      style={{
        fontFamily: theme.font.ui,
        fontSize: sizing.fontSize,
        padding: sizing.padding,
        borderRadius: theme.radius.md,
        cursor: isDisabled ? 'not-allowed' : 'pointer',
        transition: `all ${theme.motion.fast}`,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: sizing.gap,
        whiteSpace: 'nowrap',
        width: fullWidth ? '100%' : 'auto',
        opacity: isDisabled && !loading ? 0.5 : 1,
        ...variantStyles,
        ...style,
      }}
      {...rest}
    >
      {loading ? (
        <Spinner size={sizing.iconSize} color={variantStyles.color as string} />
      ) : (
        leftIcon
      )}
      {children}
      {!loading && rightIcon}
    </button>
  );
});

function Spinner({ size, color }: { size: number; color: string }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" style={{ animation: 'meridian-spin 0.8s linear infinite' }}>
      <style>{`@keyframes meridian-spin { to { transform: rotate(360deg); } }`}</style>
      <circle cx="12" cy="12" r="10" stroke={color} strokeWidth="2" fill="none" opacity="0.25" />
      <path d="M12 2a10 10 0 0 1 10 10" stroke={color} strokeWidth="2" strokeLinecap="round" fill="none" />
    </svg>
  );
}

/* ═══════════════ ICON BUTTON ═══════════════ */

interface IconButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children'> {
  icon: ReactNode;
  variant?: Variant;
  size?: Size;
  'aria-label': string;
}

export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(function IconButton(
  { icon, variant = 'ghost', size = 'md', style, disabled, onMouseEnter, onMouseLeave, ...rest },
  ref,
) {
  const sizing = {
    sm: { size: 28, iconSize: 14 },
    md: { size: 34, iconSize: 16 },
    lg: { size: 40, iconSize: 18 },
  }[size];

  const variantStyles = getVariantStyles(variant, !!disabled);

  return (
    <button
      ref={ref}
      disabled={disabled}
      onMouseEnter={e => {
        if (!disabled) Object.assign(e.currentTarget.style, getHoverStyles(variant));
        onMouseEnter?.(e);
      }}
      onMouseLeave={e => {
        if (!disabled) {
          Object.assign(e.currentTarget.style, {
            background: variantStyles.background,
            borderColor: variantStyles.border.split(' ').pop(),
            color: variantStyles.color,
          });
        }
        onMouseLeave?.(e);
      }}
      style={{
        width: sizing.size,
        height: sizing.size,
        borderRadius: theme.radius.md,
        cursor: disabled ? 'not-allowed' : 'pointer',
        transition: `all ${theme.motion.fast}`,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        opacity: disabled ? 0.5 : 1,
        ...variantStyles,
        ...style,
      }}
      {...rest}
    >
      {icon}
    </button>
  );
});
